/** @jsx CanvaSX */
import { CanvaSX, renderElement, withContext } from '@lib/canvasx/canvasx';
import WireframeBase, { BORDER_WIDTH } from './WireframeBase';
import tools from '../tools';
import wireframeToggle from '../assets/icons/wireframeToggle.svg';
import wireframeToggleRoundFilledOn from '../assets/icons/wireframeToggleRoundFilledOn.svg';
import wireframeToggleRoundHollowOn from '../assets/icons/wireframeToggleRoundHollowOn.svg';
import wireframeToggleSquareFilledOn from '../assets/icons/wireframeToggleSquareFilledOn.svg';
import wireframeToggleSquareHollowOn from '../assets/icons/wireframeToggleSquareHollowOn.svg';
import wireframeToggleRoundFilledOff from '../assets/icons/wireframeToggleRoundFilledOff.svg';
import wireframeToggleRoundHollowOff from '../assets/icons/wireframeToggleRoundHollowOff.svg';
import wireframeToggleSquareFilledOff from '../assets/icons/wireframeToggleSquareFilledOff.svg';
import wireframeToggleSquareHollowOff from '../assets/icons/wireframeToggleSquareHollowOff.svg';
import { Rect } from '@lib/canvasx/components/Rect';
import { FlexRow } from '@lib/canvasx/components/FlexRow';
import { SPACER_2 } from '../utils/style';
import { Label } from '../components/wireframes/Label';

const ROUND_RADIUS = 15;
const SQUARE_RADIUS = 3;

class WireframeToggle extends WireframeBase {
  constructor(fabricObject) {
    super(fabricObject);
    // Backwards compat: uiStates used to be default, disabled
    if (!fabricObject.uiState || fabricObject.uiState === 'default') {
      fabricObject.set('uiState', 'enabled');
    }
  }

  tools() {
    return [
      [tools.ToolWireframeComponentStyle],
      [tools.ToolWireframeComponentState],
      [tools.ToolWireframeLabelPosition],
      [tools.ToolSize],
      [tools.ToolFillColorWithoutOpacity],
      this.showLabel ? [tools.ToolWireframeTextColor] : [],
    ];
  }

  static defaultWidth(): number {
    return 40;
  }

  static defaultHeight(): number {
    return 23;
  }

  static iconSvg() {
    return wireframeToggle;
  }

  static externalName(): string {
    return 'Toggle';
  }

  static componentStyles() {
    return {
      roundOn: wireframeToggleRoundHollowOn,
      squareOn: wireframeToggleSquareHollowOn,
      roundFilledOn: wireframeToggleRoundFilledOn,
      squareFilledOn: wireframeToggleSquareFilledOn,
      roundOff: wireframeToggleRoundHollowOff,
      squareOff: wireframeToggleSquareHollowOff,
      roundFilledOff: wireframeToggleRoundFilledOff,
      squareFilledOff: wireframeToggleSquareFilledOff,
    };
  }

  componentStyles() {
    const styles = WireframeToggle.componentStyles();
    // For toggle, only show the "on" style of components. The "off" style is
    // used only in the componentState tool
    return Object.keys(styles).reduce((acc, key) => {
      if (key.includes('Off')) {
        acc[key.replace('Off', '')] = styles[key];
      }
      return acc;
    }, {});
  }

  componentStates() {
    const styles = WireframeToggle.componentStyles();
    const componentState = this.fabricObject.componentState;
    const isOn = componentState === 'on';
    const style = this.fabricObject.style + (isOn ? 'On' : 'Off');
    const otherStyle = style.replace(isOn ? 'On' : 'Off', isOn ? 'Off' : 'On');
    return {
      off: isOn ? styles[otherStyle] : styles[style],
      on: isOn ? styles[style] : styles[otherStyle],
    };
  }

  get componentStatesTitle() {
    return 'On/Off state';
  }

  get showComponentStatesTooltips() {
    return true;
  }

  static labelPositions() {
    return ['left', 'right', 'noLabel'];
  }

  labelPositions(): string[] {
    return WireframeToggle.labelPositions();
  }

  get borderRadius() {
    return this.round ? ROUND_RADIUS : SQUARE_RADIUS;
  }

  get uiStates() {
    return ['enabled', 'disabled'];
  }

  render(ctx: CanvasRenderingContext2D) {
    const {
      scaleX,
      componentState = 'on',
      uiState = 'enabled',
      textColor = 'black',
    } = this.fabricObject;
    const { filled, borderRadius, round } = this;
    const opacity = uiState === 'enabled' ? 1 : 0.4;
    const fill = this.fabricObject.fill ?? 'white';
    const borderWidth = filled ? 1 : BORDER_WIDTH;
    const padding = 5;
    const defaultWidth = WireframeToggle.defaultWidth();
    const defaultHeight = WireframeToggle.defaultHeight();
    const dim = defaultHeight - padding;

    const x =
      componentState === 'off'
        ? -defaultWidth / 2 + dim + padding
        : defaultWidth - dim - padding / 2;
    const y = defaultHeight / 2 - dim / 2;

    let renderedDimensions;

    withContext(
      {
        canvas: this.fabricObject.canvas,
        ctx,
        object: this.fabricObject,
        scale: scaleX,
      },
      () => {
        renderElement(
          <FlexRow
            verticalAlignment='center'
            gap={SPACER_2}
            setRenderedDimensions={dimensions => {
              renderedDimensions = dimensions;
            }}
          >
            {this.labelPosition() === 'left' && (
              <Label color={textColor} opacity={opacity} wireframe={this} />
            )}
            <Rect
              width={defaultWidth}
              height={defaultHeight}
              fill={{
                color: filled ? fill : 'transparent',
                opacity,
              }}
              border={{
                color: fill,
                opacity,
                lineWidth: borderWidth,
              }}
              borderRadius={borderRadius}
            >
              <Rect
                x={x}
                y={y}
                borderRadius={round ? dim : 1.2}
                width={dim}
                height={dim}
                fill={{
                  color: filled ? 'white' : fill,
                  opacity: filled ? 1 : opacity,
                }}
              />
            </Rect>
            {this.labelPosition() === 'right' && (
              <Label color={textColor} opacity={opacity} wireframe={this} />
            )}
          </FlexRow>
        );

        this._updateRenderedDimensions(renderedDimensions);
      }
    );
  }
}

export default WireframeToggle;
